import { useState } from "react";
import {
  Cloud, HardDrive, Link2, CheckCircle2, AlertCircle, Settings,
  Plus, RefreshCw, Trash2, ExternalLink, Zap, Clock,
} from "lucide-react";

const CONNECTORS = [
  {
    id: "gdrive", name: "Google Drive", icon: "GD", color: "#4285F4",
    desc: "Sync folders from Google Drive. New and updated files are automatically ingested.",
    status: "connected", lastSync: "4 hours ago", assets: 1240,
    config: { folder: "/Kadal/Higher Ed Content", autoSync: true, interval: "6h" },
  },
  {
    id: "s3", name: "Amazon S3", icon: "S3", color: "#FF9900",
    desc: "Pull assets from S3 buckets. Supports versioning and prefix-based filtering.",
    status: "connected", lastSync: "Yesterday", assets: 892,
    config: { bucket: "kadal-content-prod", prefix: "titles/", autoSync: true, interval: "12h" },
  },
  {
    id: "box", name: "Box", icon: "Bx", color: "#0061D5",
    desc: "Connect Box enterprise folders. Supports metadata-synced ingestion.",
    status: "disconnected", lastSync: null, assets: 0,
    config: null,
  },
  {
    id: "sharepoint", name: "SharePoint", icon: "SP", color: "#038387",
    desc: "Sync document libraries from Microsoft SharePoint Online.",
    status: "disconnected", lastSync: null, assets: 0,
    config: null,
  },
];

const LMS_TARGETS = [
  {
    id: "canvas", name: "Canvas LMS", icon: "C", color: "#E4060F",
    desc: "Publish approved content directly to Canvas courses as Common Cartridge packages.",
    status: "connected", published: 47, lastPublish: "2 days ago",
    config: { domain: "university.instructure.com", courseId: "BIO-101-2026S" },
  },
  {
    id: "brightspace", name: "D2L Brightspace", icon: "B", color: "#00824A",
    desc: "Export and publish packages to Brightspace learning environments.",
    status: "configured", published: 12, lastPublish: "5 days ago",
    config: { domain: "learn.university.edu", orgUnitId: "6784" },
  },
  {
    id: "moodle", name: "Moodle", icon: "M", color: "#F98012",
    desc: "Push SCORM and Common Cartridge packages to Moodle instances.",
    status: "disconnected", published: 0, lastPublish: null,
    config: null,
  },
  {
    id: "blackboard", name: "Blackboard", icon: "Bb", color: "#262626",
    desc: "Connect to Blackboard Learn for direct content delivery.",
    status: "disconnected", published: 0, lastPublish: null,
    config: null,
  },
];

const EXPORTS = [
  { id: "scorm12", name: "SCORM 1.2", desc: "Legacy LMS compatibility", enabled: true },
  { id: "scorm2004", name: "SCORM 2004", desc: "Rich sequencing & navigation", enabled: true },
  { id: "xapi", name: "xAPI (Tin Can)", desc: "Experience tracking statements", enabled: true },
  { id: "cc", name: "IMS Common Cartridge", desc: "Standard content packaging", enabled: true },
  { id: "qti", name: "QTI 2.1", desc: "Assessment interoperability", enabled: false },
  { id: "lti", name: "LTI 1.3", desc: "Tool launch integration", enabled: false },
];

const statusBadge = (s) => s === "connected" ? "bg-emerald-50 text-emerald-600 border-emerald-200" : s === "configured" ? "bg-amber-50 text-amber-600 border-amber-200" : "bg-gray-100 text-foreground/50 border-border/40";
const statusLabel = (s) => s === "connected" ? "Connected" : s === "configured" ? "Configured" : "Not Connected";

export const Integrations = () => {
  const [tab, setTab] = useState("sources");

  return (
    <div className="flex flex-col h-full" data-testid="integrations-page">
      <div className="px-6 pt-5 pb-0 flex items-center gap-4 border-b border-border/40 bg-white">
        <div className="flex bg-[#f0f4f8] rounded-full p-0.5 border border-border/40">
          {[
            { id: "sources", label: "Content Sources" },
            { id: "lms", label: "LMS Targets" },
            { id: "exports", label: "Export Formats" },
          ]?.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`text-xs font-semibold px-4 py-1.5 rounded-full ${tab === t.id ? "bg-white text-foreground shadow-sm" : "text-foreground/40 hover:text-foreground/60"}`}
              data-testid={`tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-5">
        {tab === "sources" && (
          <div className="space-y-4" data-testid="sources-list">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h2 className="font-heading font-bold text-lg text-foreground">Content Sources</h2>
                <p className="text-xs text-foreground/40 mt-0.5">Connect cloud storage to automatically ingest and sync content</p>
              </div>
              <button className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#014d7d] text-white flex items-center gap-1.5" data-testid="add-source">
                <Plus className="w-3.5 h-3.5" /> Add Source
              </button>
            </div>
            {CONNECTORS?.map((c) => (
              <div key={c.id} className="bg-white border border-border/60 rounded-2xl p-5 hover:shadow-soft" data-testid={`source-${c.id}`}>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white text-sm font-bold shrink-0" style={{ background: c.color }}>{c.icon}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-sm font-bold text-foreground">{c.name}</h3>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${statusBadge(c.status)}`}>{statusLabel(c.status)}</span>
                    </div>
                    <p className="text-xs text-foreground/40 mb-3">{c.desc}</p>
                    {c.status === "connected" && c.config && (
                      <div className="flex items-center gap-4 text-[11px] text-foreground/50">
                        <span className="flex items-center gap-1"><HardDrive className="w-3 h-3" /> {c.config.folder || c.config.bucket}</span>
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Synced {c.lastSync}</span>
                        <span className="flex items-center gap-1"><Zap className="w-3 h-3" /> {c.assets} assets</span>
                        {c.config.autoSync && <span className="text-emerald-500 font-semibold">Auto-sync {c.config.interval}</span>}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    {c.status === "connected" ? (
                      <>
                        <button className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-border/60 text-foreground/50 hover:text-foreground flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Sync Now</button>
                        <button className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-border/60 text-foreground/50 hover:text-foreground"><Settings className="w-3 h-3" /></button>
                      </>
                    ) : (
                      <button className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#014d7d] text-white flex items-center gap-1"><Link2 className="w-3 h-3" /> Connect</button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "lms" && (
          <div className="space-y-4" data-testid="lms-list">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h2 className="font-heading font-bold text-lg text-foreground">LMS Targets</h2>
                <p className="text-xs text-foreground/40 mt-0.5">Publish approved content directly to your learning management systems</p>
              </div>
              <button className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#014d7d] text-white flex items-center gap-1.5" data-testid="add-lms">
                <Plus className="w-3.5 h-3.5" /> Add LMS
              </button>
            </div>
            {LMS_TARGETS?.map((l) => (
              <div key={l.id} className="bg-white border border-border/60 rounded-2xl p-5 hover:shadow-soft" data-testid={`lms-${l.id}`}>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white text-sm font-bold shrink-0" style={{ background: l.color }}>{l.icon}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-sm font-bold text-foreground">{l.name}</h3>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${statusBadge(l.status)}`}>{statusLabel(l.status)}</span>
                    </div>
                    <p className="text-xs text-foreground/40 mb-3">{l.desc}</p>
                    {(l.status === "connected" || l.status === "configured") && l.config && (
                      <div className="flex items-center gap-4 text-[11px] text-foreground/50">
                        <span className="flex items-center gap-1"><ExternalLink className="w-3 h-3" /> {l.config.domain}</span>
                        <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {l.published} published</span>
                        {l.lastPublish && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Last {l.lastPublish}</span>}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    {l.status !== "disconnected" ? (
                      <>
                        <button className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-border/60 text-foreground/50 hover:text-foreground"><Settings className="w-3 h-3" /></button>
                      </>
                    ) : (
                      <button className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#014d7d] text-white flex items-center gap-1"><Link2 className="w-3 h-3" /> Connect</button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "exports" && (
          <div data-testid="exports-list">
            <div className="mb-4">
              <h2 className="font-heading font-bold text-lg text-foreground">Export Formats</h2>
              <p className="text-xs text-foreground/40 mt-0.5">Standards-based packaging for interoperability</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {EXPORTS?.map((ex) => (
                <div key={ex.id} className={`bg-white border rounded-2xl p-5 ${ex.enabled ? "border-border/60" : "border-border/30 opacity-60"}`} data-testid={`export-${ex.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-bold text-foreground">{ex.name}</h3>
                    <div className={`w-9 h-5 rounded-full flex items-center px-0.5 ${ex.enabled ? "bg-emerald-500 justify-end" : "bg-gray-200 justify-start"}`}>
                      <div className="w-4 h-4 rounded-full bg-white shadow-sm" />
                    </div>
                  </div>
                  <p className="text-xs text-foreground/40">{ex.desc}</p>
                  {ex.enabled && <span className="text-[10px] font-semibold text-emerald-600 mt-2 inline-block">Enabled</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
